import { useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Download } from "lucide-react";

/* ─────────────────────  MediaLightbox  ─────────────────────
 * Fullscreen overlay used to preview chat attachments at their
 * natural size. Renders inside `position: fixed` so the chat
 * scroll position behind it is irrelevant; we lock `body` scroll
 * while the dialog is open so the chat list doesn't drift on
 * mobile.
 *
 * Close behaviour:
 *   • ESC key (handled in a document keydown listener)
 *   • Tap the dimmed backdrop (full-area button behind the media)
 *   • Tap the floating X button in the top-right
 *
 * The `media` prop is `null` when closed; the AnimatePresence
 * inside this component handles the exit transition so the
 * caller doesn't have to wrap it.
 *
 * Why the caller renders `<MediaLightbox media={x} onClose={...} />`
 * unconditionally and we own the AnimatePresence:
 *   • The FamilyChatView already gates renders on thread-id,
 *     user-id, etc. — adding a separate `<AnimatePresence>` here
 *     would force that view to thread another conditional through
 *     its render path.
 *   • When `media` flips back to `null`, the spring+opacity exit
 *     runs and React unmounts. Clean lifecycle in one place.
 */

export type LightboxMedia =
  | { kind: "image"; url: string; alt: string; mime?: string }
  | { kind: "gif";   url: string; alt: string; mime?: string }
  | { kind: "video"; url: string; mime: string;       alt?: string };

export function MediaLightbox({
  media,
  onClose,
}: {
  media: LightboxMedia | null;
  onClose: () => void;
}) {
  // ESC closes the dialog while it's open.
  useEffect(() => {
    if (!media) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [media, onClose]);

  // Lock body scroll so the chat list behind doesn't drift when the
  // user scrolls the (much larger) media. Restore the previous
  // overflow on close so we don't permanently break scrolling if
  // the caller is unmounted while the dialog is still up.
  useEffect(() => {
    if (!media) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [media]);

  return (
    <AnimatePresence>
      {media && (
        <motion.div
          key="media-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={
            media.kind === "video"
              ? "影片預覽"
              : media.kind === "gif"
              ? "GIF 預覽"
              : "相片預覽"
          }
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm"
        >
          {/* Backdrop close. A full-area button BEHIND the media
              (z-0 default, media sits at z-10). Clicks on the media
              hit the media, clicks on the dimmed area hit this
              button and close the dialog. */}
          <button
            type="button"
            aria-label="關閉預覽"
            onClick={onClose}
            className="absolute inset-0 cursor-zoom-out"
          />

          {/* Floating close button. Sits above both the backdrop
              and the media so it's always reachable, including on
              mobile where the media may extend to the very edge. */}
          <div className="pointer-events-none absolute right-3 top-3 z-20 flex items-center gap-2">
            {media.kind !== "video" && (
              <a
                href={media.url}
                target="_blank"
                rel="noopener noreferrer"
                download
                aria-label="在新分頁開啟"
                className="pointer-events-auto grid h-10 w-10 place-items-center rounded-full bg-black/45 text-white shadow-md transition hover:bg-black/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                onClick={(e) => e.stopPropagation()}
              >
                <Download className="h-5 w-5" aria-hidden />
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="關閉"
              className="pointer-events-auto grid h-10 w-10 place-items-center rounded-full bg-black/45 text-white shadow-md transition hover:bg-black/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>

          {/* Media. Sits above the backdrop button via z-10 so the
              backdrop click handler doesn't fire when the user is
              actually interacting with the image / video. */}
          <motion.div
            initial={{ scale: 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="relative z-10 max-h-[88vh] max-w-[92vw]"
          >
            {(media.kind === "image" || media.kind === "gif") && (
              <img
                src={media.url}
                alt={media.alt}
                className="max-h-[88vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
                draggable={false}
              />
            )}
            {media.kind === "video" && (
              <video
                src={media.url}
                controls
                autoPlay
                playsInline
                className="max-h-[88vh] max-w-[92vw] rounded-lg shadow-2xl"
              />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}